import {
  Finishing,
  LaminationType,
  LaminationWeight,
  PageSize,
  PagesPerSheet,
  PaperWeight,
  PrintColor,
  PrintSettings,
  Settings,
  TwoSided,
} from '../../core/settings';
import { BaseModel, Price } from '../../global/models';
import { Category } from '../category/category.model';

// ─── Enums ────────────────────────────────────────────────────────────────────

/** Product type discriminator. */
export enum ProductType {
  PRINT = 'print',
  STORE = 'store',
  WALLETBONUS = 'walletbonus',
  GENERIC = 'generic',
}

/** Product subtype for store products (standard, custom, pack). */
export enum ProductSubType {
  STANDARD = 'standard',
  CUSTOM = 'custom',
  PACK = 'pack',
}

/** Custom product type (not to be confused with ProductType.CUSTOM, which is a store product subtype). */
export enum ProductCustomType {
  NOTEBOOK = 'notebook',
  RESOURCE = 'resource',
}

/** Identifiers for each customizable element in a custom store product. */
export enum ProductCustomizableElements {
  PAPER_FORMAT = 'paperFormat',
  SHEET_NUMBER = 'sheetNumber',
  RINGS = 'rings',
  COVER_FRONT = 'coverFront',
  COVER_BACK = 'coverBack',
  PDF = 'pdf',
}

/**
 * Status of a product, used to filter visibility in the storefront and for internal organization.
 */
export type ProductStatus = 'active' | 'archived' | 'draft';

export enum FileType {
  PDF = 'pdf',
  WORD = 'word',
  EXCEL = 'excel',
  POWERPOINT = 'powerpoint',
  IMAGE = 'image',
  OTHER = 'other',
}

/**
 * Status of a print file, used to track its processing state and determine when it's ready
 */
export enum PrintFileStatus {
  NEW = 'new',
  PROCESSING = 'processing',
  FINISHED = 'finished',
  FAILED = 'failed',
}

export enum PrintFileSubStatus {
  NONE = 'none',
  UPLOADING = 'uploading',
  CONVERTING = 'converting',
  COUNTING = 'counting',
  THUMBNAIL = 'thumbnail',
}

export enum CoverType {
  Color1Side1Page = 'Color1Side1Page',
  Color1Side2Page = 'Color1Side2Page',
  Color1Side4Page = 'Color1Side4Page',
  Color2Side1Page = 'Color2Side1Page',
  Color2Side2Page = 'Color2Side2Page',
  Color2Side4Page = 'Color2Side4Page',
  Black1Side1Page = 'Black1Side1Page',
  Black2Side1Page = 'Black2Side1Page',
  Black1Side2Page = 'Black1Side2Page',
  Black2Side2Page = 'Black2Side2Page',
  Black1Side4Page = 'Black1Side4Page',
  Black2Side4Page = 'Black2Side4Page',
}

// ─── Product ──────────────────────────────────────────────────────────────────
//
// Jerarquía de lectura:
//   Product (union)
//   └─ ProductPrint / ProductStore / ProductWalletBonus / ProductGeneric  ← extienden ProductBase
//   └─ ProductSnapshot                                                     ← extiende ProductBase
//      └─ ProductBase                                                      ← base compartida
//         └─ ProductVariant / PrintVariant / VendorRef / PackProductRef /
//            ProductOption / ProductExtra / ProductRef                     ← tipos de sus campos

/** Discriminated union of all product types */
export type Product = ProductPrint | ProductStore | ProductWalletBonus | ProductGeneric;

/** Product with its variants sub-collection populated (fetched from Firestore). */
export type ProductWithVariants = Omit<Product, 'variants'> & { variants: ProductVariant[]; };

/** Print-type product with printing group, weights, binding data and extra pricing config. */
export interface ProductPrint extends ProductBase {
  type: ProductType.PRINT;
  internal?: boolean; // true = system product (e.g. 'papers', 'customizablePapers'), false = user-created
  printingGroup?: PrintingGroup;
  // este bloque hace referencia a propiedades que solo se usaban en la biblioteca de contenido
  previewUrl?: string; // preview image URL for content library documents
  royalty?: number; // creator commission per unit sold, added to final price: unitPrice + extra.price + royalty
  previewPages?: number; // legacy, number of preview pages shown for content library docs
  // este bloque hace referencia a settings guardados en el product 0 (producto plantilla del sistema)
  ringColorPrice?: RingColorPrice; // extracted from product 0, maps ring color id → price per unit
  hardCoverPrice?: HardCoverPrice; // extracted from product 0, pricing tiers for hard covers
  extraPrices?: ExtraPrices; // extracted from product 0, full pricing matrix for finishing/color/extras
  ringColors?: Array<RingColor>; // available ring color options (filtered by hide=false)
  hardCovers?: Array<HardCover>; // available hard cover options for binding
  bindingData?: any; // TODO: type BindingData structure (sheetLimit, ringWeight, coverWeight) // TODO JUAN: tipado
  bindingSheetLimit?: number; // max sheets per binding before needing split (redundant with bindingData.sheetLimit)
  weights?: WeightMatrix; // paper weight matrix: weights[pageSize][paperWeight] → grams per sheet
}

/** Store-type product that may have custom/pack sub-types. */
export interface ProductStore extends ProductBase {
  type: ProductType.STORE;
  customCombination?: string; // serialized combination key for custom product pricing lookup
  customType?: string; // e.g. 'notebook', 'resource' — type of custom product
  customElements?: Array<CustomElement>; // definition of which elements can be customized (format, sheets, rings, covers, pdf)
}

/** Discriminated union of customizable element definitions in a custom store product. */
type CustomElement =
  | {
    id: ProductCustomizableElements.PAPER_FORMAT; name: string; valueType: 'simple';
    values: Array<{
      paperSize: string; paperWeight: string; paperColor: string; paperPattern: string;
      price: number; available: boolean;
      priceBySheetNumber: Array<{ sheetNumber: number; price: number; }>;
    }>;
  }
  | { id: ProductCustomizableElements.SHEET_NUMBER; name: string; valueType: 'simple'; values: Array<{ name: string; value: number; available: boolean; }>; }
  | { id: ProductCustomizableElements.RINGS; name: string; valueType: 'fixedColor'; values: CustomElementColorOption[]; }
  | { id: ProductCustomizableElements.COVER_FRONT; name: string; valueType: 'fixedColor'; values: CustomElementColorOption[]; }
  | { id: ProductCustomizableElements.COVER_BACK; name: string; valueType: 'fixedColor'; values: CustomElementColorOption[]; };

/** Color option shape shared by rings, coverFront and coverBack custom elements. */
interface CustomElementColorOption {
  id: string; name: string; color: string; available: boolean;
  position: number; internalId: string | number;
  category?: string; exclusive?: boolean; soldOut?: boolean;
}
export interface ProductWalletBonus extends ProductBase {
  type: ProductType.WALLETBONUS;
}

export interface ProductGeneric extends ProductBase {
  type: ProductType.GENERIC;
}

/** Snapshot of a product at the time of adding to cart/order */
export interface ProductSnapshot extends ProductBase {
  variantId?: string;
  customElementsSelected?: CustomElementsSelected;
  customCombination?: string;
  printingGroup?: PrintingGroup;
  royalty?: number;
}

export interface CustomElementsSelected {
  paperWeight: {
    name: string,
    value: string,
  };
  paperColor: {
    name: string,
    value: string,
    data: {
      color: string;
    };
  };
  paperPattern: {
    name: string,
    value: string,
  };
  paperSize: {
    name: string,
    value: string,
  };
  sheetNumber: {
    name: string,
    value: string,
    available: boolean,
  };
  rings: RingColor;
  coverFront: HardCover;
  coverBack: HardCover;
}

/** Shared base fields for all product discriminated-union members. */
export interface ProductBase extends BaseModel {
  // Pricing / inventory
  unitPrice: Price; // base price before discounts (amt + cur), tax included
  discount: number; // percentage discount 0–100, applied as unitPrice * (1 - discount/100)
  hideDiscount: boolean; // suppress discount badge in UI even if discount > 0 (useful for internal discounts)
  cost: Price; // COGS / internal cost (what the seller paid), used for margin calculation
  stock: number; // available inventory qty; undefined = unlimited stock
  soldQty: number; // total units sold, shown as social proof ("X veces impreso" / "X ventas")
  allowSaleNoStock: boolean; // allow back-orders when stock ≤ 0; max qty in cart: allowSaleNoStock ? 100 : stock
  sku: string; // Stock Keeping Unit, unique inventory identifier
  requiresShipping: boolean; // whether shipping costs apply to this product
  freeShipping?: boolean; // product-level free shipping flag, shows "Envío gratis" in UI
  weight: number; // product weight in grams; for print products overridden by printingGroup.weight
  pictureUrl?: string; // primary product image URL
  pictureSecondaryUrl?: string; // secondary/hover product image URL
  pictureId?: string; // storage file ID for primary image
  pictureSecondaryId?: string; // storage file ID for secondary image
  copyPrice?: number; // internal price reference for current option combination, set from getPriceOption(settings)

  // Identity
  type: ProductType;
  listOrder: number; // sort position in product listings (lower = first)
  title: string;
  description: string; // rich text product description
  descriptionLines?: Array<object>; // structured description items displayed as boxed detail lines
  mediaFiles: string[]; // array of media file URLs/IDs for the product gallery
  slug: string; // URL-friendly identifier for SEO routes (e.g. /papeleria/catalogo/{slug})
  seo: { title: string; description: string; }; // page title and meta description for search engines
  featured: boolean; // highlighted product, appears first in listings and can be filtered
  categories: Category[];
  labels: any[]; //Esto parece no se usa // TODO Al eliminarlo se rompen varios componentes por eso lo dejo por ahora
  status: ProductStatus; // 'active' = published, 'draft' = unpublished, 'archived' = hidden
  options: Array<ProductOption>; // configurable option definitions, e.g. [{ name: 'Color', values: ['Red','Blue'] }]
  optionsValue?: Array<string>; // currently selected option values, matched against variant.optionsValue to find variant
  properties?: object; // variant-specific key-value properties for custom products (e.g. { paperWeight, paperColor })
  variantCount: number; // number of variants; 0 or falsy = single variant (use base price)
  variants?: string[] | PrintVariant[]; // variant document IDs in Firestore (ordering preserved); populated as ProductVariant[] via ProductWithVariants
  vendor: VendorRef; // seller/creator reference (id, name, thumbnail, slug)
  packProducts?: Array<PackProductRef>; // products included in a bundle/pack with quantities
  commissionable?: boolean; // qualifies for ambassador/creator commission payout
  exclusive: boolean; // exclusive/limited product, shows "Exclusivo" label and uses exclusive pricing tiers
  relevance: number; // search/ranking priority score for sorting
  presale: boolean; // pre-order flag, shows "Preventa" badge; cannot mix presale + regular items in cart
  categoryScore: number; // category-specific ranking boost for sort within a category
  internalId?: string; // factory/warehouse system reference ID
  folderName?: string; // user-editable label for print file groups in cart (e.g. "Carpeta 1")
  extra?: ProductExtra; // additional finishing/pricing info (price added to unitPrice, blank sheets, wallet bonus amount)
  subType?: ProductSubType;
  customType?: string;
}

/** A store product variant with its own pricing, stock and media. */
export interface ProductVariant extends ProductBase {
  visible?: boolean; // whether this variant is shown in the storefront
  optionsValue: string[]; // selected option values identifying this variant, e.g. ['Red', 'Large']
}

/** Combination of print options → unit price used in the pricing table. */
export interface PrintVariant {
  options: PrintOption;
  price: number;
}

export type PrintOption = [PrintColor, PageSize, PaperWeight, TwoSided, PagesPerSheet];

/** Lightweight reference to a vendor (seller / shop). */
export interface VendorRef {
  id: string;
  name: string;
  thumbnail: string;
  slug?: string;
}

/**
 * Reference to a product included within a pack-type product, with quantity and optional variation info.
 */
export interface PackProductRef {
  productId: string;
  qty: number;
  variationId?: string;
  variationProps?: string;
}

export interface ProductOption {
  name: string; // option label, e.g. 'Color', 'Talla'
  values: Array<string>; // possible values, e.g. ['Red', 'Blue', 'Green']
  galleries?: Array<{ name: string; gallery: Array<string>; }>; // per-value image galleries for option-linked media
}

/** Extra finishing/pricing info attached to a product */
export interface ProductExtra {
  price?: number; // extra cost added to unitPrice for finishing/binding (finalPrice = unitPrice + extra.price + royalty)
  description?: string; // label describing the extra, shown in cart/order summaries
  units?: number; // number of extras applied (e.g. binding count per group)
  amount?: number; //only for wallet bonus, represents the amount of money the bonus is worth
  currency?: string; //only for wallet bonus, represents the currency of the amount
  blankSheetsUnits?: number; // auto-calculated number of blank sheets inserted
  blankSheetsPrice?: number; // auto-calculated total cost for blank sheets (units × price × blankSheetPercentageCalc/100)
}

/** Lightweight reference to a product (id + optional title). */
export interface ProductRef {
  id: string;
  title?: string;
}

// ─── Print files ──────────────────────────────────────────────────────────────
//
// Jerarquía de lectura:
//   PrintingGroup                ← agrupa los archivos de una impresión
//   └─ PrintFile                 ← archivo base (clase, debe ir antes de _PrintFile)
//      └─ ThumbSetting           ← thumbnails de PrintFile
//         └─ _StoredFile         ← referencia a fichero almacenado
//   └─ _PrintFile                ← archivo con opciones de impresión aplicadas
//      └─ CoverTypeConfig        ← configuración de portada
//      └─ RingColor              ← color de anilla seleccionado
//      └─ HardCover              ← tapa dura seleccionada
//   └─ BindingSummary            ← resumen de encuadernación (usado en PrintingGroup y _PrintFile)
//      └─ BindingRingUsage       ← consumo de anillas
//      └─ BindingCoverUsage      ← consumo de tapas

/**
 * Groups all print files together with shared print settings.
 * Represents the printable portion of a print-type product or cart item.
 */
export class PrintingGroup {
  printSettings: PrintSettings; // shared print config: color, paper size/weight, finishing, lamination, etc.
  comment: string; // user notes for the printer; prefixed with "SKIP_FIT_TO_PAGE:" if skip-fit enabled
  files: _PrintFile[]; // all print files in this group with their individual settings applied
  printSides?: number; // calculated total sides across all files (sum of file sides)
  printSheets?: number; // calculated total physical sheets including blank sheets
  binding?: BindingSummary; // aggregated binding info for the whole group (ring/cover usage, splits)
  weight?: number; // calculated total weight in grams for shipping (sheets × weight-per-sheet + binding weight)
}

export interface PrintFile extends BaseModel {
  originalFile: _StoredFile; // reference to the user-uploaded file (may be docx, pptx, image, etc.)
  originalType: FileType; // format of the uploaded file (pdf, word, excel, powerpoint, image, other)
  printFile: _StoredFile; // system-converted print-ready PDF (result of conversion pipeline)
  pages: number; // original document page count
  thumbFile: _StoredFile; // generated thumbnail preview image
  status: PrintFileStatus; // processing pipeline status: NEW → PROCESSING → FINISHED | FAILED
  errorMessage?: string; // error description when status = FAILED, shown to user
  orderId?: string; // links file to containing order when embedded in order data
  substatus?: PrintFileSubStatus; // granular processing step: UPLOADING → CONVERTING → COUNTING → THUMBNAIL
  thumbs?: Array<ThumbSetting>; // pre-generated thumbnail variants for different print setting combinations
  thumbFiles?: Array<ThumbSetting>; // alias for thumbs, deleted before database save to reduce payload
  printProtected?: boolean; // PDF has print-restriction flag set
  printAllowed?: boolean; // printing allowed despite printProtected flag (user override)
  pswProtected?: boolean; // PDF is password-protected, blocks processing
}

export interface ThumbSetting {
  printSettings: Pick<
    PrintSettings,
    Settings.PAGE_ORIENTATION | Settings.COLOR | Settings.COVER_COLOR |
    Settings.PAGES_PER_SHEET | Settings.AUTO_ROTATE | Settings.PAGE_TURN
  >;
  file: _StoredFile;
}

/** Minimal stored file reference used within print files */
export class _StoredFile {
  name: string; // original file name with extension
  contentType: string; // MIME type, e.g. 'application/pdf'
  path: string; // storage path in cloud bucket
  size: number; // file size in bytes
  url: string; // public download URL
}

export interface _PrintFile extends PrintFile {
  coverColor?: boolean; // true = cover printed in color, false = B&W cover
  coverLaminated?: boolean; // true = cover is laminated (forces coverColor=true)
  docColor?: boolean; // true = interior pages in color; auto-set if coverColor && pages===1
  binding?: BindingSummary; // per-file binding result (ring/cover usage, split info)
  blankSheetsBefore?: number; // blank sheets inserted before this file's content
  blankSheetsAfter?: number; // blank sheets inserted after this file's content
  printSheets?: number; // total physical sheets for this file (content sheets + blank sheets, accounting for duplex)
  alias?: string; // user-editable display name, falls back to originalFile.name
  ringColor?: RingColor; // selected ring color for this file's binding
  hardCoverFront?: HardCover; // selected front hard cover for this file's binding
  hardCoverBack?: HardCover; // selected back hard cover for this file's binding
  coverType?: CoverTypeConfig; // cover page configuration (color, paper weight, pages-per-sheet, two-sided)
  laminationType?: LaminationType; // lamination finish type (Gloss, Matte, SemiGloss)
  laminationWeight?: LaminationWeight; // lamination thickness in microns (W80, W125, W150)
  total?: number; // calculated price component for this file (base file cost + file-specific finishing, NOT group finishing)
}

export interface CoverTypeConfig {
  coverColor: boolean; // whether cover is printed in color
  pagesPerSheet: PagesPerSheet; // how many pages fit on one cover sheet (1, 2, or 4)
  id: string; // CoverType enum value, e.g. 'Color2Side4Page'
  twoSided: boolean; // whether cover is printed on both sides
  paperWeight: PaperWeight; // paper weight used for the cover
  paperWeightChanged?: PaperWeight; // overridden paper weight if user modified it from default
  default?: boolean; // true if this is the auto-selected default cover configuration
}

export interface RingColor {
  name: string; // name to show in spanish, e.g. 'Transparente', 'Rojo', 'Azul'
  color: string; //hexadecimal color code
  id: string; //internal id referencing color in english, e.g. 'Red', 'Blue', 'Transparent'
  hide?: boolean; // used to hide options that are not currently available but may be in the future
  category?: string; // used to group colors in the UI, e.g. 'Standard', 'Premium', 'Transparent'
  internalId: string; // numeric id used in factory to find the shelf with this color
  position: number; // used to sort colors in the UI
  soldOut?: boolean; // runtime-only flag, set when stock is depleted; not persisted
}

export interface HardCover {
  name: string; // name to show in spanish, e.g. 'Transparente', 'Rojo', 'Azul'
  color: string; //hexadecimal color code
  id: string; //internal id referencing color in english, e.g. 'Red', 'Blue', 'Transparent'
  hide?: boolean; // used to hide options that are not currently available but may be in the future
  category?: string; // used to group colors in the UI, e.g. 'Standard', 'Premium', 'Transparent'
  internalId: string; // numeric id used in factory to find the shelf with this color
  position: number; // used to sort colors in the UI
  exclusive?: boolean; // used to mark hard covers that are Copyfly's exclusive and should be highlighted as such in the UI
  soldOut?: boolean; // runtime-only flag, set when stock is depleted; not persisted
}

/**
 * Summary of binding results for a file or printing group.
 * Contains ring/cover usage, split info (when sheets exceed the binding limit),
 * ring diameter and total binding weight.
 */
export interface BindingSummary {
  rings?: BindingRingUsage[]; // ring units consumed by color and diameter
  covers?: BindingCoverUsage[]; // hard cover units consumed by color and page size
  count: number; // number of binding jobs (>1 if sheets exceed limit and binding is split)
  ringSplit: boolean; // true if binding was split into multiple jobs due to sheet limit
  ringDiameter: number; // diameter of the ring used (mm)
  weight: number; // total binding weight in grams (rings + covers)
}

/** Track how many ring units of a given color+diameter are consumed by a binding job. */
export interface BindingRingUsage {
  color: string;
  diameter: number;
  name: string;
  uses: number;
}

/** Track how many hard-cover units of a given color+pageSize are consumed by a binding job. */
export interface BindingCoverUsage {
  color: string;
  name: string;
  pageSize: PageSize;
  uses: number;
}

// ─── Print pricing ────────────────────────────────────────────────────────────
//
// Jerarquía de lectura:
//   ExtraPrices                  ← matriz de precios extra (guardada en ProductPrint)
//   └─ ExtraFinishingOption      ← opción de acabado con precio
//      └─ ExtraBase              ← base compartida (descripción + precio)
//      └─ FinishingVariant       ← variante concreta de acabado (laminado, tamaño, etc.)
//   RingColorPrice / HardCoverPrice / WeightMatrix  ← tablas de precios planas

/** Aggregated extra-pricing matrix indexed by finishing type and setting flags used in product 0. */
export interface ExtraPrices {
  blankSheetPercentageCalc: number; // percentage (e.g. 50–70) applied to sheet price for blank sheet cost calculation
  finishing: {
    [Finishing.BINDING]: ExtraFinishingOption;
    [Finishing.STAPLED]: ExtraFinishingOption;
    [Finishing.LAMINATED]: ExtraFinishingOption;
    [Finishing.PERFORATED2]: ExtraFinishingOption;
    [Finishing.PERFORATED4]: ExtraFinishingOption;
    [Finishing.PERFORATEDBIND]: ExtraFinishingOption;
    [Finishing.NONE]: ExtraFinishingOption;
  };
  coverColor: { true: ExtraBase; false: ExtraBase; }; // surcharge when cover is color vs B&W
  docColor: { true: ExtraBase; false: ExtraBase; }; // surcharge when interior pages are color vs B&W
  skipFitToPage: { true: ExtraBase; }; // surcharge for disabling fit-to-page (manual scaling)
  [key: string]: any;
}

/** Extra pricing option for a finishing type (binding, stapled, laminated, etc.). */
export interface ExtraFinishingOption extends ExtraBase {
  description: string; // display label for this finishing type
  limit: number; // max sheets supported by this finishing (e.g. binding limit)
  minPrice?: number; // minimum price floor for this finishing option
  variants?: FinishingVariant[]; // sub-variants with lamination/pageSize-specific pricing
  weight: number; // weight in grams added by this finishing operation
}

export interface ExtraBase {
  description: string;
  price: number;
}

/** A single finishing variant with lamination + page-size options and its price/weight. */
export interface FinishingVariant {
  options: {
    laminationType: LaminationType;
    laminationWeight: LaminationWeight;
    pageSize: PageSize;
  };
  price: number;
  weight: number;
}

/** Price map for ring colors. Keys are color ids; 'Default' is the fallback. */
export interface RingColorPrice {
  Transparente: number;
  Default: number;
  [key: string]: number;
}

/** Price map for hard covers by variant. */
export interface HardCoverPrice {
  default: number; // price for standard default hard covers
  noDefault: number; // price for non-default hard covers
  exclusive: number; // price for Copyfly-exclusive hard covers (premium tier)
}

/** Weight lookup matrix: weights[pageSize][paperWeight] → weight in grams */
export type WeightMatrix = Record<string, Record<string, number>>;

// ─── UI Models ────────────────────────────────────────────────────────────────
//
// Jerarquía de lectura:
//   PrintProductPageUI  ← vista de detalle para producto de impresión
//   └─ ProductUI        ← modelo UI unificado (lista/tarjeta y detalle, extiende ProductBase)
//   DocumentListUI      ← vista de lista para documentos (plana)

/** Extended product-page model for print-type products (adds royalty and total).
 * !esto es de la biblioteca de contenido
*/
export interface PrintProductPageUI extends ProductUI {
  unitPrice: Price; // base print price (sheets × price-per-sheet)
  royalty: number; // creator commission per unit
  total: number; // final calculated total: unitPrice + extra.price + royalty
}

/** Unified UI model for products, used in both list/card views and the detail page.
 *  Extends ProductBase — only declares fields not already present there.
 */
export interface ProductUI extends ProductBase {
  // Computed display state (always populated)
  price: Price;                           // calculated display price (after discount, variant selection)
  isSoldOut: boolean;                     // computed: !allowSaleNoStock && stock ≤ 0
  hasVariant: boolean;                    // computed: variantCount > 0
  isCustom: boolean;                      // computed: subType === 'custom'
  // List-view fields (populated by toProductListUI)
  thumbnail?: string;                     // resolved primary image URL for card display
  thumbnail2?: string;                    // resolved secondary/hover image URL for card display
  cheapestCombinationPrice?: number;      // lowest price from custom product option combinations
  // Runtime / computed (not persisted, calculated client-side from variants)
  highestDiscount?: number;               // max discount % across all variants, displayed as "Hasta -X%"
  cheapestVariant?: ProductVariant;       // variant with lowest final price, used to show "Desde X€"
  variantId?: string;                     // currently selected variant ID
  // Page/detail-view fields (populated by product-handler)
  qty?: number;                           // quantity selected by user for add-to-cart
  canAdd?: boolean;                       // whether the product can be added to cart
  visible?: boolean;                      // whether product should be visible (variant-level)
  customElementsSelected?: CustomElementsSelected; // current user selections for customizable product elements
}

export type ProductWithVariantsUI = Omit<ProductUI, 'variants'> & { variants: ProductVariant[]; };


/** UI model for a document (print-type product) in a list. */
export interface DocumentListUI {
  id: string;
  title: string;
  vendor: VendorRef;
  pictureUrl: string;
  unitPrice: Price;
  extra: ProductExtra;
  slug: string;
  soldQty: number;
  status: ProductStatus;
  price: Price;
  thumbnail: string;
}
